/**
 * `useActivityPing` — fires a single Peitho `POST /v1/activity/ping` per local
 * day for the authenticated user. This is what feeds the "accessed the app on
 * N distinct days" referral milestone (`access_days`) — a referred user only
 * qualifies their inviter after opening the app on 3 distinct days.
 *
 * Debounce contract (pure `shouldPingToday`):
 *   - localStorage key `cuidari.activity.lastPing` holds the YYYY-MM-DD of the
 *     last successful ping.
 *   - We only call the network when today's date != the stored value.
 *   - On success we persist today's date, so subsequent opens the same day are
 *     no-ops (zero network).
 *
 * Failure is silent: a 401/5xx/network error never touches the app and never
 * advances the stored date, so the next open retries.
 *
 * Mounted once in `AnalyticsProvider`, which renders only inside the
 * authenticated tree (below `AuthGate`), so the ping is naturally gated on a
 * real session.
 */
import { useEffect, useRef } from 'react'
import { useAuth } from '@/lib/cerberus/useAuth'
import { pingActivity } from './api'

export const ACTIVITY_PING_KEY = 'cuidari.activity.lastPing'

/** Local-day stamp, YYYY-MM-DD, in the user's timezone. */
export function todayStamp(now: Date = new Date()): string {
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Pure debounce decision — exported for unit tests. Returns true only when the
 * stored last-ping stamp differs from today (including the never-pinged case).
 */
export function shouldPingToday(
  lastStored: string | null,
  today: string,
): boolean {
  return lastStored !== today
}

function readLastPing(): string | null {
  try {
    return localStorage.getItem(ACTIVITY_PING_KEY)
  } catch {
    return null
  }
}

function writeLastPing(stamp: string): void {
  try {
    localStorage.setItem(ACTIVITY_PING_KEY, stamp)
  } catch {
    /* quota / private mode — next open retries */
  }
}

/**
 * Run the once-per-day ping for the authenticated user. Side-effect only;
 * returns nothing. Idempotent within a day (localStorage guard) and across
 * re-renders (a ref blocks duplicate in-flight calls within a mount).
 */
export function useActivityPing(): void {
  const { status } = useAuth()
  const firedRef = useRef(false)

  useEffect(() => {
    if (status !== 'authed') return
    if (firedRef.current) return

    const today = todayStamp()
    if (!shouldPingToday(readLastPing(), today)) return

    firedRef.current = true
    const controller = new AbortController()
    void pingActivity('cuidari', { signal: controller.signal })
      .then(() => {
        writeLastPing(today)
      })
      .catch(() => {
        // Silent — do not advance the stamp so the next open retries.
        firedRef.current = false
      })

    return () => controller.abort()
  }, [status])
}
